import { useEffect, useRef, useState } from 'react';
import { fetchFrame, type Turn } from '../lib/api';
import { holdAt, needsSeek } from '../lib/playback';

// ---------------------------------------------------------------------------
// What the selected line will look like.
//
// Drawn by the same code the renderer uses — the preview service runs the
// render image with a different entrypoint and calls the same _draw_frame — so
// this cannot drift from the finished video the way a second, hand-written
// mock-up would. Change the panel geometry and both move together.
//
// It draws the frame and nothing else: no audio, no motion, no transition into
// the next line. Those need ffmpeg and are not what a layout decision waits on.
// ---------------------------------------------------------------------------

/** Long enough that typing a caption is one request, short enough to feel live. */
const DEBOUNCE_MS = 400;

export function FramePreview({ cardId, line, rundown, title, overlay, into = null, readout, motion = null, caption = null }: {
  cardId: string;
  line: number | null;
  rundown: Turn[];
  title: string;
  /**
   * The join the playhead is inside: the next line and how far into the
   * crossfade. The next frame is drawn over this one at that opacity, which
   * is what a dissolve IS — so scrubbing through a join shows the join.
   */
  into?: { line: number; alpha: number } | null;
  /** Where the playhead is, in words: the time, the scene, what is on screen. */
  readout?: string | null;
  /**
   * What sits over the frame — the layers on this scene and any the director
   * has proposed for it.
   *
   * Passed in rather than fetched, because this component draws the frame and
   * should not also decide what belongs on top of it. It is a stage, and the
   * caller says what is standing on it.
   */
  overlay?: React.ReactNode;
  /**
   * The scene's own footage, playing, while the transport runs.
   *
   * The still underneath is kept and drawn first, so the stage is never blank
   * while a clip opens. When the clip cannot be reached at all the still is
   * simply what stays on screen, which is the old behaviour and not an error.
   */
  motion?: { url: string; at: number; playing: boolean; muted: boolean } | null;
  /** The line, over the footage, the way the render burns it in. */
  caption?: string | null;
}) {
  // A scene that cuts to footage is drawn too, now: the API hands the preview
  // the footage's small copy and it is drawn under the caption the way the
  // render burns it. It used to say "nothing here that the render would
  // show", which read as a blank preview — because it was one.
  const [url, setUrl] = useState<string | null>(null);
  const [intoUrl, setIntoUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [drawing, setDrawing] = useState(false);

  // The URL currently painted, held in a ref so the cleanup can revoke the one
  // being replaced without the effect depending on it — which would re-run the
  // effect on its own result, and fetch forever.
  const painted = useRef<string | null>(null);
  const video = useRef<HTMLVideoElement | null>(null);
  // Bumped when the element learns its own duration: until then there is no
  // end to hold at, and the effect below would let the clip run past it.
  const [ready, setReady] = useState(0);

  // The clock owns the time; the video follows it. Correcting every frame
  // would re-seek the decoder every frame, so it is corrected only when it has
  // drifted visibly — and a paused transport is pinned exactly.
  const wanted = motion?.at ?? 0;
  const running = Boolean(motion?.playing);
  useEffect(() => {
    const el = video.current;
    if (!el || !motion) return;
    // A scene outlives its clip by the beat after the line, and a scene can be
    // held far longer on purpose. Both end the same way: the last frame stays
    // up, because that is what the renderer cuts.
    const { at, held } = holdAt(wanted, Number.isFinite(el.duration) ? el.duration : null);
    if (!running || held || needsSeek(el.currentTime, at)) {
      try { el.currentTime = at; } catch { /* not seekable yet; the next tick tries again */ }
    }
    if (running && !held) { void el.play().catch(() => undefined); } else { el.pause(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [motion?.url, running, wanted, ready]);

  const turn = line === null ? null : rundown[line];
  // The frame depends on exactly these. Deriving the dependency rather than
  // listing objects: `rundown` is a new array on every keystroke anywhere in
  // the script, and depending on it would redraw line 3 when line 9 is typed.
  const signature = turn
    ? JSON.stringify([title, rundown.length, rundown.map((t) => t.speaker), turn])
    : null;

  useEffect(() => {
    if (line === null || !signature) { setError(null); return; }
    const ac = new AbortController();
    const timer = setTimeout(() => {
      setDrawing(true);
      fetchFrame(cardId, line, { rundown, title }, ac.signal)
        .then((next) => {
          if (ac.signal.aborted) { URL.revokeObjectURL(next); return; }
          if (painted.current) URL.revokeObjectURL(painted.current);
          painted.current = next;
          setUrl(next);
          setError(null);
        })
        .catch((err: unknown) => {
          if (ac.signal.aborted) return;
          const e = err as { status?: number; message?: string };
          setError(e.status === 503
            ? 'Preview is offline. The render still works.'
            : e.message ?? 'could not draw that frame');
        })
        .finally(() => { if (!ac.signal.aborted) setDrawing(false); });
    }, DEBOUNCE_MS);
    return () => { ac.abort(); clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardId, line, signature]);

  // The frame being dissolved INTO, fetched only while the playhead is inside
  // a join. Its opacity is the join's progress; nothing else about it moves.
  const intoLine = into?.line ?? null;
  useEffect(() => {
    if (intoLine === null || !rundown[intoLine]) { setIntoUrl(null); return; }
    const ac = new AbortController();
    fetchFrame(cardId, intoLine, { rundown, title }, ac.signal)
      .then((next) => { if (ac.signal.aborted) URL.revokeObjectURL(next); else setIntoUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return next; }); })
      .catch(() => { if (!ac.signal.aborted) setIntoUrl(null); });
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardId, intoLine, signature]);

  // Revoke on unmount. An object URL lives as long as the document, and this
  // one is a megabyte of PNG.
  useEffect(() => () => { if (painted.current) URL.revokeObjectURL(painted.current); }, []);
  useEffect(() => () => { if (intoUrl) URL.revokeObjectURL(intoUrl); }, [intoUrl]);

  return (
    <div className="framepv">
      <div className="framepv-hd">
        <span className="framepv-title">
          {line === null ? 'Frame preview' : `Line ${line + 1}`}
        </span>
        {readout && <span className="framepv-readout">{readout}</span>}
        {drawing && <span className="framepv-note">drawing…</span>}
      </div>
      {line === null && <p className="framepv-empty">Select a line to see its frame, or click the ruler.</p>}
      {line !== null && error && <p className="framepv-error">{error}</p>}
      {line !== null && !error && url && (
        <div className="framepv-stage">
          <img className="framepv-img" src={url} alt={`Frame for line ${line + 1}`} />
          {into && intoUrl && (
            <img className="framepv-into" src={intoUrl} alt="" style={{ opacity: Math.min(1, Math.max(0, into.alpha)) }} />
          )}
          {motion && (
            <>
              <video ref={video} className="framepv-motion" src={motion.url}
                     muted={motion.muted} playsInline preload="auto"
                     onLoadedMetadata={() => setReady((n) => n + 1)} />
              {caption && <span className="framepv-caption">{caption}</span>}
            </>
          )}
          {overlay}
        </div>
      )}
      {line !== null && !error && !url && <div className="framepv-skeleton" />}
    </div>
  );
}
